(() => {
  if (customElements.get('gm-cookie-consent')) return;
  const categories = ['preferences', 'analytics', 'marketing'];
  class GiaCookieConsent extends HTMLElement {
    connectedCallback() {
      this.banner = this.querySelector('.gm-cookie-banner');
      this.dialog = this.querySelector('dialog');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('click', (event) => {
        const action = event.target.closest('[data-cookie-action]')?.dataset.cookieAction;
        if (!action || this.busy) return;
        if (action === 'preferences') this.openPreferences(event.target);
        if (action === 'close') this.dialog.close();
        if (action === 'accept' || action === 'reject') this.save(Object.fromEntries(categories.map((key) => [key, action === 'accept'])));
        if (action === 'save') {
          const consent = Object.fromEntries(categories.map((key) => [key, this.querySelector(`[name="${key}"]`).checked]));
          if (!this.querySelector('[data-cookie-sharing]').hidden) consent.sale_of_data = this.querySelector('[name="sale_of_data"]').checked;
          this.save(consent);
        }
      }, options);
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-gm-cookie-preferences], a[href$="#shopifyReshowConsentBanner"]');
        if (!trigger || !this.api) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openPreferences(trigger);
      }, { ...options, capture: true });
      this.dialog.addEventListener('cancel', (event) => { if (this.busy) event.preventDefault(); }, options);
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('gm-cookie-modal-open');
        if (!this.completed && this.needsConsent) this.banner.hidden = false;
        if (this.returnFocus?.isConnected && !this.returnFocus.closest('[hidden]')) this.returnFocus.focus({ preventScroll: true });
      }, options);
      document.addEventListener('visitorConsentCollected', () => {
        if (this.busy || !this.api) return;
        this.refresh();
      }, options);
      this.startTime = Date.now();
      this.load();
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.loadTimer);
      clearTimeout(this.saveTimer);
      document.documentElement.classList.remove('gm-cookie-ready');
      document.body.classList.remove('gm-cookie-modal-open');
    }
    load() {
      const privacy = window.Shopify?.customerPrivacy;
      if (privacy?.setTrackingConsent && privacy.currentVisitorConsent && privacy.shouldShowBanner) {
        this.api = privacy;
        try {
          this.refresh();
          document.documentElement.classList.add('gm-cookie-ready');
        } catch (_) { this.api = null; }
        return;
      }
      if (Date.now() - this.startTime > 12000) return;
      if (window.Shopify?.loadFeatures && !this.requested) {
        this.requested = true;
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
          if (!error && this.isConnected) this.load();
        });
      }
      this.loadTimer = setTimeout(() => this.load(), 250);
    }
    refresh() {
      const consent = this.api.currentVisitorConsent();
      const preview = new URLSearchParams(window.location.search).get('preview_cookie_banner') === '1';
      this.needsConsent = preview || (this.api.shouldShowBanner() && categories.some((key) => consent[key] !== 'yes' && consent[key] !== 'no'));
      this.banner.hidden = !this.needsConsent || this.dialog.open;
    }
    openPreferences(trigger) {
      if (!this.api || this.busy || this.dialog.open) return;
      const consent = this.api.currentVisitorConsent();
      categories.forEach((key) => { this.querySelector(`[name="${key}"]`).checked = consent[key] === 'yes'; });
      const sharing = this.querySelector('[data-cookie-sharing]');
      sharing.hidden = !this.api.saleOfDataRegion?.();
      if (!sharing.hidden) {
        const input = this.querySelector('[name="sale_of_data"]');
        input.checked = consent.sale_of_data === 'yes' || (consent.sale_of_data === '' && this.api.saleOfDataAllowed?.() === true);
        input.disabled = navigator.globalPrivacyControl === true;
        if (input.disabled) input.checked = false;
      }
      this.clearErrors();
      this.completed = false;
      this.returnFocus = trigger;
      this.banner.hidden = true;
      document.body.classList.add('gm-cookie-modal-open');
      this.dialog.showModal();
    }
    clearErrors() {
      this.querySelectorAll('.gm-cookie-error').forEach((element) => { element.hidden = true; element.textContent = ''; });
    }
    setBusy(busy) {
      this.busy = busy;
      this.querySelectorAll('button').forEach((button) => { button.disabled = busy; });
      this.setAttribute('aria-busy', String(busy));
    }
    save(consent) {
      if (!this.api || this.busy) return;
      this.clearErrors();
      this.setBusy(true);
      let settled = false;
      const finish = (error) => {
        if (settled || !this.isConnected) return;
        settled = true;
        clearTimeout(this.saveTimer);
        this.setBusy(false);
        let confirmed = false;
        try {
          const stored = this.api.currentVisitorConsent();
          confirmed = Object.entries(consent).every(([key, value]) => stored[key] === (value ? 'yes' : 'no') || (key === 'sale_of_data' && navigator.globalPrivacyControl === true && stored[key] === 'no'));
        } catch (_) {}
        if (error || !confirmed) {
          const errorElement = (this.dialog.open ? this.dialog : this.banner).querySelector('.gm-cookie-error');
          errorElement.textContent = this.dataset.error;
          errorElement.hidden = false;
          return;
        }
        this.completed = true;
        this.needsConsent = false;
        this.banner.hidden = true;
        if (this.dialog.open) this.dialog.close();
        this.querySelector('[data-cookie-status]').textContent = this.dataset.saved;
      };
      this.saveTimer = setTimeout(() => finish(new Error('Consent save timed out')), 15000);
      try { this.api.setTrackingConsent(consent, (result) => finish(result?.error)); }
      catch (error) { finish(error); }
    }
  }
  customElements.define('gm-cookie-consent', GiaCookieConsent);
})();
